import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, Button, useToast, Badge } from '@powercore/ui';
import { Mic, Hash, Linkedin, Users, RotateCcw, ChevronDown, ChevronUp, Save, Loader2, RefreshCw, Eye, EyeOff, Sparkles, Database, CreditCard, TrendingUp, AlertCircle, ExternalLink, Check, Zap, AlertTriangle, XCircle } from 'lucide-react';
import { api, type VoiceProfile, type LinkedInConnection } from '../lib/api';
import { useAuth } from '../platform';
import { LoadingOverlay } from '../components/LoadingOverlay';

interface VoiceSample {
  id: string;
  content: string;
  type: string;
  created_at: string;
}

interface UserSettings {
  voiceProfile: VoiceProfile | null;
  voiceSamples: VoiceSample[];
  topicsInclude: string[];
  topicsExclude: string[];
  linkedinConnected: boolean;
}

interface BillingStatus {
  subscription: {
    user_id: string;
    plan_id: string;
    stripe_customer_id?: string;
    stripe_subscription_id?: string;
    status: string;
    trial_ends_at?: string;
    current_period_start?: string;
    current_period_end?: string;
    cancel_at_period_end?: boolean;
  } | null;
  plan: {
    id: string;
    name: string;
    price_cents: number;
  } | null;
}

interface UsageData {
  feature: string;
  usage: number;
  limit?: number;
  access: boolean;
  percentUsed: number;
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regeneratingVoice, setRegeneratingVoice] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [settings, setSettings] = useState<UserSettings>({
    voiceProfile: null,
    voiceSamples: [],
    topicsInclude: [],
    topicsExclude: [],
    linkedinConnected: false,
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showVoiceDetails, setShowVoiceDetails] = useState(false);
  const [showSamples, setShowSamples] = useState(false);
  const [knowledgeCount, setKnowledgeCount] = useState(0);

  // Billing state
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
  const [usage, setUsage] = useState<UsageData[]>([]);
  const [availablePlans, setAvailablePlans] = useState<any[]>([]);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [trialStatus, setTrialStatus] = useState<{
    isTrial: boolean;
    trialExpired: boolean;
    daysRemaining: number | null;
  } | null>(null);

  // Editable state
  const [topicsInclude, setTopicsInclude] = useState<string[]>([]);
  const [topicsExclude, setTopicsExclude] = useState<string[]>([]);
  const [newTopicInclude, setNewTopicInclude] = useState('');
  const [newTopicExclude, setNewTopicExclude] = useState('');

  // LinkedIn state
  const [linkedInStatus, setLinkedInStatus] = useState<LinkedInConnection | null>(null);
  const [linkedInLoading, setLinkedInLoading] = useState(false);
  const [linkedInDisconnecting, setLinkedInDisconnecting] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      loadSettings();
    }
  }, [isAuthenticated]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const [statusResult, voiceResult, knowledgeResult, billingResult, usageResult, plansResult, trialResult, linkedInResult] = await Promise.all([
        api.getOnboardingStatus(),
        api.getVoiceProfile(),
        api.listKnowledge({ limit: 1 }),
        api.getBillingStatus(),
        api.getBillingUsage(),
        api.getAvailablePlans(),
        api.getTrialStatus().catch(() => null),
        api.getLinkedInStatus().catch(() => null),
      ]);

      if (statusResult.data && voiceResult.data) {
        const status = statusResult.data.data;
        const voice = voiceResult.data.data;

        setSettings({
          voiceProfile: voice.voice_profile,
          voiceSamples: voice.voice_samples || [],
          topicsInclude: status.topics_include || [],
          topicsExclude: status.topics_exclude || [],
          linkedinConnected: false,
        });
        setTopicsInclude(status.topics_include || []);
        setTopicsExclude(status.topics_exclude || []);
      }

      if (knowledgeResult.data) {
        setKnowledgeCount(knowledgeResult.data.total || 0);
      }

      if (billingResult.data) {
        const billingData = (billingResult.data as any)?.data || billingResult.data;
        setBillingStatus({
          subscription: billingData.subscription || null,
          plan: billingData.plan || null,
        });
      }

      if (usageResult.data) {
        // Transform usage data to include percentUsed and feature name
        const rawUsage = (usageResult.data as any)?.data || usageResult.data || [];
        const transformedUsage = rawUsage.map((item: any) => {
          const isUnlimited = item.limit >= 99999;
          return {
            feature: item.featureName || item.featureId,
            usage: item.usage || 0,
            limit: isUnlimited ? undefined : item.limit,
            access: isUnlimited ? true : item.usage < item.limit,
            percentUsed: isUnlimited ? 0 : (item.limit > 0 ? (item.usage / item.limit) * 100 : 0),
          };
        });
        setUsage(transformedUsage);
      }

      if (plansResult.data) {
        setAvailablePlans((plansResult.data as any)?.data || plansResult.data);
      }

      if (trialResult?.data) {
        const trialData = (trialResult.data as any)?.data || trialResult.data;
        setTrialStatus(trialData);
      }

      if (linkedInResult?.data) {
        const liData = (linkedInResult.data as any)?.data || linkedInResult.data;
        setLinkedInStatus(liData);
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
    setLoading(false);
  };

  // Handle LinkedIn OAuth callback
  useEffect(() => {
    if (!isAuthenticated) return;
    const linkedInParam = searchParams.get('linkedin');
    const errorParam = searchParams.get('error');

    if (linkedInParam === 'connected') {
      searchParams.delete('linkedin');
      setSearchParams(searchParams, { replace: true });
      addToast({
        title: 'LinkedIn connected!',
        description: 'You can now publish posts directly to LinkedIn.',
        variant: 'success',
      });
      // Reload LinkedIn status
      api.getLinkedInStatus().then((result) => {
        if (result.data) {
          const liData = (result.data as any)?.data || result.data;
          setLinkedInStatus(liData);
        }
      });
    } else if (errorParam === 'linkedin_scope_error') {
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
      addToast({
        title: 'LinkedIn permissions not available',
        description: 'The LinkedIn app needs "Share on LinkedIn" product enabled. Please contact the admin.',
        variant: 'error',
      });
    } else if (errorParam === 'linkedin_auth_failed') {
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
      addToast({
        title: 'LinkedIn connection failed',
        description: 'Something went wrong connecting to LinkedIn. Please try again.',
        variant: 'error',
      });
    }
  }, [isAuthenticated]);

  const handleLinkedInConnect = async () => {
    setLinkedInLoading(true);
    try {
      const result = await api.getLinkedInAuthUrl();
      const authUrl = (result.data as any)?.data?.authUrl || (result.data as any)?.authUrl;
      if (authUrl) {
        window.location.href = authUrl;
      } else if (result.error) {
        addToast({
          title: 'Connection failed',
          description: result.error.message,
          variant: 'error',
        });
      }
    } catch {
      addToast({
        title: 'Connection failed',
        description: 'Could not initiate LinkedIn connection.',
        variant: 'error',
      });
    }
    setLinkedInLoading(false);
  };

  const handleLinkedInDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect LinkedIn?')) return;
    setLinkedInDisconnecting(true);
    try {
      const result = await api.disconnectLinkedIn();
      if (!result.error) {
        setLinkedInStatus({ connected: false, status: 'disconnected' });
        addToast({
          title: 'LinkedIn disconnected',
          description: 'Your LinkedIn account has been disconnected.',
          variant: 'default',
        });
      } else {
        addToast({
          title: 'Disconnect failed',
          description: result.error.message,
          variant: 'error',
        });
      }
    } catch {
      addToast({
        title: 'Disconnect failed',
        description: 'Could not disconnect LinkedIn.',
        variant: 'error',
      });
    }
    setLinkedInDisconnecting(false);
  };

  // Verify checkout on return from Stripe
  useEffect(() => {
    if (!isAuthenticated) return;
    const upgraded = searchParams.get('upgraded');
    const sessionId = sessionStorage.getItem('mazo-checkout-session-id');

    if (upgraded === 'true' && sessionId) {
      sessionStorage.removeItem('mazo-checkout-session-id');
      // Remove the query param to avoid re-verifying on refresh
      searchParams.delete('upgraded');
      setSearchParams(searchParams, { replace: true });

      // Verify the checkout with the backend
      (async () => {
        try {
          const result = await api.verifyCheckout(sessionId);
          const data = (result.data as any)?.data || result.data;
          if (data?.verified) {
            addToast({
              title: 'Upgrade successful!',
              description: `You're now on the ${data.planId} plan.`,
              variant: 'success',
            });
            // Reload settings to reflect the new plan
            loadSettings();
            // Notify App.tsx to refresh the header billing badge
            window.dispatchEvent(new Event('billing-updated'));
          } else {
            // Checkout wasn't paid yet - might need a moment
            addToast({
              title: 'Payment processing',
              description: 'Your payment is being processed. Refresh in a moment.',
              variant: 'default',
            });
          }
        } catch (err) {
          console.error('Failed to verify checkout:', err);
        }
      })();
    } else if (upgraded === 'true') {
      // No session ID stored but upgraded=true - just clean up the URL
      searchParams.delete('upgraded');
      setSearchParams(searchParams, { replace: true });
    }
  }, [isAuthenticated]);

  const handleUpgradeToPlan = async (planId: string) => {
    console.log('handleUpgradeToPlan called with planId:', planId);
    setLoadingBilling(true);
    setRedirecting(true); // Show full-screen loading overlay

    try {
      console.log('Calling createCheckoutSession API...');
      const result = await api.createCheckoutSession(planId);
      console.log('Checkout session result:', result);

      const checkoutData = (result.data as any)?.data || result.data;
      const checkoutUrl = checkoutData?.checkoutUrl;
      console.log('Checkout URL:', checkoutUrl);

      if (checkoutUrl) {
        // Store session ID so we can verify on return
        const sessionId = checkoutData?.sessionId;
        if (sessionId) {
          sessionStorage.setItem('mazo-checkout-session-id', sessionId);
        }
        // Redirect to Stripe checkout
        console.log('Redirecting to Stripe checkout...');
        window.location.assign(checkoutUrl);
        return; // Don't hide redirecting - we're navigating away
      }

      // Checkout failed - log the error
      console.error('No checkout URL returned. Result:', result);
      if (result.error) {
        console.error('API error:', result.error);
      }
    } catch (err) {
      console.error('Checkout exception:', err);
    }

    // Checkout failed - show error and stop loading
    setRedirecting(false);
    setLoadingBilling(false);

    addToast({
      title: 'Checkout unavailable',
      description: 'There was an issue creating the checkout session. Check console for details.',
      variant: 'error',
    });
  };

  const handleManageBilling = async () => {
    setLoadingBilling(true);
    setRedirecting(true);

    try {
      const result = await api.getBillingPortalUrl();
      const portalData = (result.data as any)?.data || result.data;
      const portalUrl = portalData?.portalUrl;

      if (portalUrl) {
        window.location.assign(portalUrl);
        return; // Keep redirecting=true, we're navigating away
      }

      setRedirecting(false);
      setLoadingBilling(false);
      addToast({ title: 'Error', description: result.error?.message || 'Failed to open billing portal', variant: 'error' });
    } catch (err) {
      setRedirecting(false);
      setLoadingBilling(false);
      addToast({ title: 'Error', description: 'Failed to open billing portal', variant: 'error' });
    }
  };

  const handleSaveTopics = async () => {
    setSaving(true);
    try {
      const result = await api.setTopicPreferences({
        topics_include: topicsInclude,
        topics_exclude: topicsExclude,
      });
      if (!result.error) {
        setSettings(prev => ({
          ...prev,
          topicsInclude,
          topicsExclude,
        }));
        addToast({
          title: 'Topics saved',
          description: 'Your topic preferences have been updated.',
          variant: 'success',
        });
      } else {
        addToast({
          title: 'Error',
          description: result.error.message,
          variant: 'error',
        });
      }
    } catch (err) {
      addToast({
        title: 'Error',
        description: 'Failed to save topics',
        variant: 'error',
      });
    }
    setSaving(false);
  };

  const handleRegenerateVoice = async () => {
    if (settings.voiceSamples.length === 0) {
      addToast({
        title: 'No samples available',
        description: 'Please add voice samples first by going through onboarding.',
        variant: 'error',
      });
      return;
    }

    setRegeneratingVoice(true);
    try {
      const result = await api.generateVoiceProfile();
      if (result.data) {
        setSettings(prev => ({
          ...prev,
          voiceProfile: result.data!.data.voice_profile,
        }));
        addToast({
          title: 'Voice profile regenerated',
          description: 'Your voice profile has been updated based on your samples.',
          variant: 'success',
        });
      } else if (result.error) {
        addToast({
          title: 'Error',
          description: result.error.message,
          variant: 'error',
        });
      }
    } catch (err) {
      addToast({
        title: 'Error',
        description: 'Failed to regenerate voice profile',
        variant: 'error',
      });
    }
    setRegeneratingVoice(false);
  };

  const addTopic = (type: 'include' | 'exclude') => {
    const topic = type === 'include' ? newTopicInclude.trim() : newTopicExclude.trim();
    if (!topic) return;

    if (type === 'include') {
      if (!topicsInclude.includes(topic)) {
        setTopicsInclude([...topicsInclude, topic]);
      }
      setNewTopicInclude('');
    } else {
      if (!topicsExclude.includes(topic)) {
        setTopicsExclude([...topicsExclude, topic]);
      }
      setNewTopicExclude('');
    }
  };

  const removeTopic = (type: 'include' | 'exclude', topic: string) => {
    if (type === 'include') {
      setTopicsInclude(topicsInclude.filter(t => t !== topic));
    } else {
      setTopicsExclude(topicsExclude.filter(t => t !== topic));
    }
  };

  const handleRedoOnboarding = async () => {
    if (!confirm('This will reset your voice profile, topics, and voice samples. Are you sure?')) return;

    try {
      await api.resetOnboarding();
      // Clear ALL onboarding sessionStorage so it starts completely fresh
      try {
        sessionStorage.removeItem('mazo-onboarding-current-step');
        sessionStorage.removeItem('mazo-onboarding-transitioning');
        sessionStorage.removeItem('mazo-onboarding-last-click');
        sessionStorage.removeItem('mazo-onboarding-loaded');
        sessionStorage.removeItem('mazo-onboarding-generating');
      } catch (e) {
        // sessionStorage not available
      }
      navigate('/onboarding');
    } catch (err) {
      addToast({
        title: 'Error',
        description: 'Failed to reset onboarding',
        variant: 'error',
      });
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-gray-500">Please sign in to access settings.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-mazo-400" />
      </div>
    );
  }

  return (
    <>
      {/* Loading overlay for voice regeneration */}
      <LoadingOverlay
        isLoading={regeneratingVoice}
        title="Regenerating Voice Profile"
        description="AI is analyzing your writing samples to update your voice profile"
        statusText="Analyzing writing patterns..."
        icon="sparkles"
      />

      {/* Loading overlay for billing redirect */}
      <LoadingOverlay
        isLoading={redirecting}
        title="Opening Stripe Checkout"
        description="Redirecting you to secure payment page..."
        statusText="Please wait"
        icon="spinner"
      />

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-500 mt-1">
            Customize how Mazo creates content for you
          </p>
        </div>

        {/* Voice Profile */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-50">
                <Mic className="h-5 w-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <CardTitle>Voice Profile</CardTitle>
                <p className="text-sm text-gray-500">Your unique writing style and tone</p>
              </div>
              {settings.voiceProfile && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRegenerateVoice}
                  disabled={regeneratingVoice || settings.voiceSamples.length === 0}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Regenerate
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {settings.voiceProfile ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-green-50 text-green-700">
                    Active
                  </Badge>
                  <span className="text-sm text-gray-600">
                    Your voice profile is set up and ready
                  </span>
                </div>

                {/* Voice Profile Summary */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Tone</p>
                    <p className="text-sm text-gray-900">{settings.voiceProfile.tone}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Style</p>
                    <p className="text-sm text-gray-900">{settings.voiceProfile.style}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Formality</p>
                    <p className="text-sm text-gray-900 capitalize">{settings.voiceProfile.formality}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Narrative</p>
                    <p className="text-sm text-gray-900 capitalize">{settings.voiceProfile.narrativeStyle}</p>
                  </div>
                </div>

                {/* Toggle full details */}
                <button
                  onClick={() => setShowVoiceDetails(!showVoiceDetails)}
                  className="flex items-center gap-2 text-sm text-mazo-500 hover:text-mazo-600"
                >
                  {showVoiceDetails ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  {showVoiceDetails ? 'Hide details' : 'Show full profile'}
                </button>

                {showVoiceDetails && (
                  <div className="space-y-4 pt-2 border-t">
                    {/* Sentence Structure */}
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase mb-1">Sentence Structure</p>
                      <p className="text-sm text-gray-700">{settings.voiceProfile.sentenceStructure}</p>
                    </div>

                    {/* Patterns */}
                    {settings.voiceProfile.patterns && settings.voiceProfile.patterns.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">Writing Patterns</p>
                        <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                          {settings.voiceProfile.patterns.map((pattern, i) => (
                            <li key={i}>{pattern}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Signature Phrases */}
                    {settings.voiceProfile.signaturePhrases && settings.voiceProfile.signaturePhrases.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">Signature Phrases</p>
                        <div className="flex flex-wrap gap-2">
                          {settings.voiceProfile.signaturePhrases.map((phrase, i) => (
                            <span key={i} className="px-2 py-1 bg-purple-50 text-purple-700 rounded text-sm">
                              "{phrase}"
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Vocabulary */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">Preferred Words</p>
                        <div className="flex flex-wrap gap-1">
                          {settings.voiceProfile.vocabulary?.preferred?.map((word, i) => (
                            <span key={i} className="px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs">
                              {word}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">Words to Avoid</p>
                        <div className="flex flex-wrap gap-1">
                          {settings.voiceProfile.vocabulary?.avoid?.map((word, i) => (
                            <span key={i} className="px-2 py-0.5 bg-red-50 text-red-700 rounded text-xs">
                              {word}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Voice Samples */}
                {settings.voiceSamples.length > 0 && (
                  <div className="pt-4 border-t">
                    <button
                      onClick={() => setShowSamples(!showSamples)}
                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
                    >
                      {showSamples ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      {showSamples ? 'Hide' : 'Show'} voice samples ({settings.voiceSamples.length})
                    </button>

                    {showSamples && (
                      <div className="mt-3 space-y-2">
                        {settings.voiceSamples.map((sample) => (
                          <div key={sample.id} className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="secondary" className="text-xs">
                                {sample.type}
                              </Badge>
                              <span className="text-xs text-gray-400">
                                {new Date(sample.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 line-clamp-3">{sample.content}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-900">Voice profile not set up yet</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Without a voice profile, Mazo cannot generate posts that match your writing style.
                        Complete the quick setup to start creating authentic content.
                      </p>
                    </div>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/onboarding')}
                  className="w-full text-white"
                  style={{ backgroundColor: 'rgb(var(--color-mazo-500))' }}
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Complete your voice setup
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Topics */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-mazo-50">
                <Hash className="h-5 w-5 text-mazo-600" />
              </div>
              <div>
                <CardTitle>Topics</CardTitle>
                <p className="text-sm text-gray-500">What you want to post about</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Topics to Include */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Topics to focus on
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {topicsInclude.map((topic) => (
                  <span
                    key={topic}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-mazo-50 text-mazo-700 rounded text-sm"
                  >
                    {topic}
                    <button
                      onClick={() => removeTopic('include', topic)}
                      className="hover:text-mazo-900"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTopicInclude}
                  onChange={(e) => setNewTopicInclude(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addTopic('include')}
                  placeholder="Add a topic..."
                  className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-mazo-500"
                />
                <Button variant="outline" size="sm" onClick={() => addTopic('include')}>
                  Add
                </Button>
              </div>
            </div>

            {/* Topics to Exclude */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Topics to avoid
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {topicsExclude.map((topic) => (
                  <span
                    key={topic}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-red-50 text-red-700 rounded text-sm"
                  >
                    {topic}
                    <button
                      onClick={() => removeTopic('exclude', topic)}
                      className="hover:text-red-900"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTopicExclude}
                  onChange={(e) => setNewTopicExclude(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addTopic('exclude')}
                  placeholder="Add a topic to avoid..."
                  className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-mazo-500"
                />
                <Button variant="outline" size="sm" onClick={() => addTopic('exclude')}>
                  Add
                </Button>
              </div>
            </div>

            <Button onClick={handleSaveTopics} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Topics
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Knowledge Base */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-50">
                <Database className="h-5 w-5 text-green-600" />
              </div>
              <div className="flex-1">
                <CardTitle>Knowledge Base</CardTitle>
                <p className="text-sm text-gray-500">Extracted insights from your sources</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-green-50 text-green-700">
                    {knowledgeCount} items
                  </Badge>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  View and manage your extracted knowledge items, insights, stories, and frameworks.
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/knowledge')}>
                <Database className="h-4 w-4 mr-2" />
                Manage
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Billing & Subscription */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-50">
                <CreditCard className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <CardTitle>Billing & Subscription</CardTitle>
                <p className="text-sm text-gray-500">Manage your plan and usage</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/pricing')}>
                View All Plans
              </Button>
              {/* Manage button for paid users */}
              {billingStatus?.subscription?.plan_id && billingStatus.subscription.plan_id !== 'free' && billingStatus.subscription.stripe_customer_id && (
                <Button variant="outline" size="sm" onClick={handleManageBilling} disabled={loadingBilling}>
                  {loadingBilling ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ExternalLink className="h-4 w-4 mr-2" />Manage</>}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {billingStatus ? (
              <div className="space-y-6">
                {/* Trial Status Banner (from /api/billing/trial-status) */}
                {trialStatus?.trialExpired && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-900">Your free trial has ended</p>
                      <p className="text-sm text-red-700 mt-0.5">
                        Upgrade to continue using Mazo features.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      className="bg-red-600 hover:bg-red-700 text-xs"
                      onClick={() => navigate('/pricing')}
                    >
                      <Zap className="h-3 w-3 mr-1" />
                      Upgrade Now
                    </Button>
                  </div>
                )}
                {trialStatus?.isTrial && !trialStatus.trialExpired && trialStatus.daysRemaining != null && (
                  <div className={`flex items-start gap-2 p-3 rounded-lg border ${
                    trialStatus.daysRemaining <= 7
                      ? 'bg-yellow-50 border-yellow-200'
                      : 'bg-blue-50 border-blue-200'
                  }`}>
                    <Sparkles className={`h-4 w-4 flex-shrink-0 mt-0.5 ${
                      trialStatus.daysRemaining <= 7 ? 'text-yellow-600' : 'text-blue-600'
                    }`} />
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${
                        trialStatus.daysRemaining <= 7 ? 'text-yellow-900' : 'text-blue-900'
                      }`}>
                        Your free trial ends in {trialStatus.daysRemaining} {trialStatus.daysRemaining === 1 ? 'day' : 'days'}
                        {trialStatus.daysRemaining <= 7 ? ' \u2014 upgrade now' : ''}
                      </p>
                    </div>
                    {trialStatus.daysRemaining <= 7 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-yellow-700 border-yellow-300 hover:bg-yellow-100 text-xs"
                        onClick={() => navigate('/pricing')}
                      >
                        <Zap className="h-3 w-3 mr-1" />
                        Upgrade
                      </Button>
                    )}
                  </div>
                )}

                {/* Legacy Trial Banner (from subscription status) */}
                {!trialStatus && billingStatus.subscription?.status === 'trialing' &&
                  billingStatus.subscription.trial_ends_at &&
                  (() => {
                    const trialEnd = new Date(billingStatus.subscription.trial_ends_at!);
                    const now = new Date();
                    const isExpired = trialEnd < now;
                    const daysRemaining = Math.ceil(
                      (trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
                    );
                    const planName = billingStatus.plan?.name || billingStatus.subscription.plan_id;

                    return isExpired ? (
                      <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-red-900">Your trial has expired</p>
                          <p className="text-sm text-red-700 mt-0.5">
                            Upgrade to continue using {planName} features.
                          </p>
                        </div>
                        <Button
                          size="sm"
                          className="bg-red-600 hover:bg-red-700 text-xs"
                          onClick={() => navigate('/pricing')}
                        >
                          <Zap className="h-3 w-3 mr-1" />
                          Upgrade Now
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <Sparkles className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-blue-900">
                            You're on a free trial of {planName}
                          </p>
                          <p className="text-sm text-blue-700 mt-0.5">
                            {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining.
                            Upgrade to keep your access after the trial ends.
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-blue-700 border-blue-300 hover:bg-blue-100 text-xs"
                          onClick={() => handleUpgradeToPlan(billingStatus.subscription!.plan_id)}
                          disabled={loadingBilling}
                        >
                          <Zap className="h-3 w-3 mr-1" />
                          Upgrade Now
                        </Button>
                      </div>
                    );
                  })()}

                {/* Plan Cards */}
                {(() => {
                  const currentPlanId = billingStatus.subscription?.plan_id ?? 'free';

                  // Map API data to UI format
                  const plans = availablePlans.map((plan) => {
                    const price = plan.price_cents === 0
                      ? '€0'
                      : `€${(plan.price_cents / 100).toFixed(0)}`;
                    const period = plan.price_cents === 0
                      ? 'forever'
                      : `/${plan.billing_interval}`;

                    // Build features list from plan features
                    const features = plan.features?.map((f: any) => {
                      const isUnlimited = f.limit_value >= 99999;
                      if (f.feature_id === 'posts_generated') {
                        return isUnlimited ? `Unlimited posts / ${plan.billing_interval}` : `${f.limit_value} posts / ${plan.billing_interval}`;
                      } else if (f.feature_id === 'sources_connected') {
                        return isUnlimited ? 'Unlimited sources' : `${f.limit_value} ${f.limit_value === 1 ? 'source' : 'sources'}`;
                      }
                      return isUnlimited ? `${f.feature_name}: Unlimited` : `${f.feature_name}: ${f.limit_value}`;
                    }) || [];

                    // Add "Priority support" for paid plans
                    if (plan.price_cents > 0) {
                      features.push('Priority support');
                    }

                    return {
                      id: plan.id,
                      name: plan.name,
                      price,
                      period,
                      features,
                      highlight: plan.id === 'growth', // Highlight middle plan
                    };
                  });
                  return (
                    <div className="grid grid-cols-3 gap-3">
                      {plans.map((plan) => {
                        const isCurrent = currentPlanId === plan.id;
                        const isUpgrade = plan.id !== 'free' && !isCurrent;
                        return (
                          <div
                            key={plan.id}
                            className={`relative rounded-xl border-2 p-4 transition-all ${
                              isCurrent
                                ? 'border-mazo-500 bg-mazo-50'
                                : plan.highlight
                                ? 'border-gray-200 bg-white shadow-sm'
                                : 'border-gray-200 bg-white'
                            }`}
                          >
                            {plan.highlight && !isCurrent && (
                              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-mazo-500 text-white text-xs font-medium px-2.5 py-0.5 rounded-full">
                                Popular
                              </span>
                            )}
                            {isCurrent && (
                              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-mazo-600 text-white text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1">
                                <Check className="h-3 w-3" /> Current
                              </span>
                            )}
                            <div className="mb-3">
                              <p className="font-semibold text-gray-900">{plan.name}</p>
                              <div className="flex items-baseline gap-1 mt-1">
                                <span className="text-2xl font-bold text-gray-900">{plan.price}</span>
                                <span className="text-xs text-gray-500">{plan.period}</span>
                              </div>
                            </div>
                            <ul className="space-y-1.5 mb-4">
                              {plan.features.map((f: string) => (
                                <li key={f} className="flex items-center gap-1.5 text-xs text-gray-600">
                                  <Check className="h-3 w-3 text-mazo-500 flex-shrink-0" />
                                  {f}
                                </li>
                              ))}
                            </ul>
                            {isCurrent ? (
                              <div className="text-center text-xs text-mazo-600 font-medium py-1.5">
                                Current plan
                              </div>
                            ) : isUpgrade ? (
                              <Button
                                size="sm"
                                className="w-full bg-mazo-500 hover:bg-mazo-600 text-xs"
                                onClick={() => handleUpgradeToPlan(plan.id)}
                                disabled={loadingBilling}
                              >
                                {loadingBilling ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <><Zap className="h-3 w-3 mr-1" />Upgrade</>
                                )}
                              </Button>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* Usage Stats */}
                {usage.length > 0 && (
                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-gray-400" />
                      <p className="text-sm font-medium text-gray-700">Usage this month</p>
                    </div>
                    {usage.map((item, index) => (
                      <div key={item.feature || index} className="space-y-1.5">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600 capitalize">
                            {item.feature?.replace(/_/g, ' ') || 'Unknown'}
                          </span>
                          <span className="font-medium text-gray-900">
                            {item.limit ? `${item.usage} / ${item.limit}` : `${item.usage} / Unlimited`}
                          </span>
                        </div>
                        {item.limit && (
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${
                                item.percentUsed >= 90
                                  ? 'bg-red-500'
                                  : item.percentUsed >= 70
                                  ? 'bg-yellow-500'
                                  : 'bg-mazo-500'
                              }`}
                              style={{ width: `${Math.min(item.percentUsed, 100)}%` }}
                            />
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Warning if exceeded limit */}
                    {usage.some((item) => item.percentUsed > 100) && (
                      <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mt-3">
                        <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-red-900">Limit exceeded</p>
                          <p className="text-sm text-red-700 mt-0.5">
                            You've exceeded your plan limits. Upgrade to continue adding sources and generating posts.
                          </p>
                        </div>
                        <Button
                          size="sm"
                          className="bg-red-600 hover:bg-red-700 text-xs"
                          onClick={() => navigate('/pricing')}
                        >
                          <Zap className="h-3 w-3 mr-1" />
                          Upgrade Now
                        </Button>
                      </div>
                    )}

                    {/* Warning if approaching limit */}
                    {usage.some((item) => item.percentUsed >= 80 && item.percentUsed <= 100) && (
                      <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg mt-3">
                        <AlertCircle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-yellow-900">Approaching limit</p>
                          <p className="text-sm text-yellow-700 mt-0.5">
                            Upgrade your plan to get more posts and sources
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* LinkedIn Connection */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: '#E8F1FB' }}>
                <Linkedin className="h-5 w-5" style={{ color: '#0A66C2' }} />
              </div>
              <div>
                <CardTitle>LinkedIn Connection</CardTitle>
                <p className="text-sm text-gray-500">Publish directly to LinkedIn</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {linkedInStatus?.connected && linkedInStatus.status === 'active' ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    {linkedInStatus.displayName && (
                      <p className="font-medium text-gray-900">{linkedInStatus.displayName}</p>
                    )}
                    {linkedInStatus.email && (
                      <p className="text-sm text-gray-500">{linkedInStatus.email}</p>
                    )}
                    <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                      <Check className="h-3 w-3" />
                      Connected
                      {linkedInStatus.daysUntilExpiry != null && (
                        <span className="text-gray-400 ml-1">
                          &middot; Expires in {linkedInStatus.daysUntilExpiry} days
                        </span>
                      )}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLinkedInDisconnect}
                    disabled={linkedInDisconnecting}
                  >
                    {linkedInDisconnecting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Disconnect'
                    )}
                  </Button>
                </div>
                {linkedInStatus.daysUntilExpiry != null && linkedInStatus.daysUntilExpiry <= 7 && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                    <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-yellow-800">
                        Your LinkedIn connection expires in {linkedInStatus.daysUntilExpiry} day{linkedInStatus.daysUntilExpiry !== 1 ? 's' : ''}. Reconnect to avoid interruption.
                      </p>
                      <button
                        onClick={handleLinkedInConnect}
                        disabled={linkedInLoading}
                        className="text-sm text-yellow-700 underline hover:text-yellow-900 mt-1 inline-flex items-center gap-1"
                      >
                        {linkedInLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                        Re-authenticate
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : linkedInStatus?.connected && linkedInStatus.status === 'expired' ? (
              <div className="space-y-3">
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
                  <XCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-red-800 font-medium">LinkedIn connection expired</p>
                    {linkedInStatus.displayName && (
                      <p className="text-sm text-red-600">{linkedInStatus.displayName}</p>
                    )}
                  </div>
                </div>
                <Button
                  onClick={handleLinkedInConnect}
                  disabled={linkedInLoading}
                  className="w-full"
                  style={{ backgroundColor: '#0A66C2' }}
                >
                  {linkedInLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Linkedin className="h-4 w-4 mr-2" />
                  )}
                  Reconnect LinkedIn
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-500">
                  Publish posts directly to LinkedIn from your Publish page.
                </p>
                <Button
                  onClick={handleLinkedInConnect}
                  disabled={linkedInLoading}
                  className="w-full text-white"
                  style={{ backgroundColor: '#0A66C2' }}
                >
                  {linkedInLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Linkedin className="h-4 w-4 mr-2" />
                  )}
                  Connect LinkedIn
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Refer Friends */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-50">
                <Users className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <CardTitle>Refer Friends</CardTitle>
                <p className="text-sm text-gray-500">Invite others to try Mazo</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <Badge variant="secondary" className="bg-gray-100 text-gray-600">
                  Coming Soon
                </Badge>
                <p className="text-sm text-gray-500 mt-2">
                  Share Mazo with your network and earn rewards.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Advanced Section */}
        <div className="border-t pt-4">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
          >
            {showAdvanced ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
            Advanced options
          </button>

          {showAdvanced && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Redo Onboarding</h4>
                  <p className="text-sm text-gray-500">
                    Reset your voice profile and start fresh
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={handleRedoOnboarding}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Reset
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
